import { StyleSheet, View } from 'react-native';
import { Badge, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { CallStructuredSummary, InterestLevel } from '../../voice-agents/types';

interface CallSummaryCardProps {
  summary: CallStructuredSummary;
  interestLevel?: InterestLevel | null;
  closingProbability?: number | null;
}

function interestLevelColor(level: InterestLevel): 'green' | 'amber' | 'red' {
  if (level === 'hot') return 'green';
  if (level === 'warm') return 'amber';
  return 'red';
}

function SummaryField({
  label,
  value,
}: Readonly<{ label: string; value: string | number | undefined | null }>) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <View style={styles.fieldRow} accessible accessibilityLabel={`${label}: ${value}`}>
      <Text variant="caption" color={colors.text.tertiary} style={styles.fieldLabel}>
        {label}
      </Text>
      <Text variant="bodySmall" color={colors.text.primary} style={styles.fieldValue}>
        {String(value)}
      </Text>
    </View>
  );
}

export function CallSummaryCard({
  summary,
  interestLevel,
  closingProbability,
}: Readonly<CallSummaryCardProps>) {
  const { t } = useTranslation();

  return (
    <View
      style={styles.root}
      accessible
      accessibilityLabel={t('leads.callResults.structuredSummary')}
    >
      {(interestLevel || (closingProbability !== null && closingProbability !== undefined)) && (
        <HStack spacing="sm" align="center" style={styles.metricsRow}>
          {interestLevel && (
            <Badge
              variant="subtle"
              color={interestLevelColor(interestLevel)}
              size="sm"
              accessibilityLabel={`${t('leads.callResults.interestLevel')}: ${t(`leads.callResults.interestLevel_${interestLevel}`)}`}
            >
              {t(`leads.callResults.interestLevel_${interestLevel}`)}
            </Badge>
          )}
          {closingProbability !== null && closingProbability !== undefined && (
            <Text
              variant="caption"
              color={colors.text.secondary}
              accessibilityLabel={`${t('leads.callResults.closingProbability')}: ${closingProbability}%`}
            >
              {t('leads.callResults.closingProbability')}: {closingProbability}%
            </Text>
          )}
        </HStack>
      )}

      <VStack spacing="sm">
        <SummaryField label={t('leads.callResults.fields.resumen')} value={summary.resumen} />
        <SummaryField
          label={t('leads.callResults.fields.dolor_principal')}
          value={summary.dolor_principal}
        />
        <SummaryField label={t('leads.callResults.fields.necesidad')} value={summary.necesidad} />
        <SummaryField
          label={t('leads.callResults.fields.presupuesto')}
          value={summary.presupuesto}
        />
        <SummaryField
          label={t('leads.callResults.fields.timeline_decision')}
          value={summary.timeline_decision}
        />
        <SummaryField
          label={t('leads.callResults.fields.propuesta_sugerida')}
          value={summary.propuesta_sugerida}
        />
        <SummaryField
          label={t('leads.callResults.fields.proximos_pasos')}
          value={summary.proximos_pasos}
        />
      </VStack>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    gap: tokens.spacing.md,
  },
  metricsRow: {
    flexWrap: 'wrap',
    marginBottom: tokens.spacing.xs,
  },
  fieldRow: {
    gap: tokens.spacing.xxs,
  },
  fieldLabel: {
    textTransform: 'uppercase',
    fontSize: 10,
    letterSpacing: 0.5,
    fontWeight: '600',
  },
  fieldValue: {
    lineHeight: 20,
  },
});
